import React, { useEffect } from 'react';
import PropTypes from 'prop-types';
import { connect, useDispatch, useSelector } from 'react-redux';
import styled from 'styled-components';

import { Button, Modal } from 'antd';

import { colors } from '../../utils/variables.styles';

import EditableSection from '../../components/EditableSection';
import SelectEdit from '../../components/SelectEdit';

import selector from './Roofing.selector';

import { infoShape } from '../../types/info.types';
import { availableType } from '../../types/available.types';

import {
  setSelected as setSelectedParameter,
  setRoofingExtension,
} from '../Construction/construction.reducer';
import { Info } from '../ToolingEditor/ToolingEditor.styles';
import withTranslation from "../../components/WithTranslation/WithTranslation";
import { InfoCircleOutlined } from "@ant-design/icons";

const Container = styled.div`
  background: ${colors.white};
  padding: 10px 24px;
  margin-left: -27px;
`;

const Spacer = styled.div`
  padding: 10px 24px;
  font-weight: 600;
  max-width: 280px;
`;

const ImgWrapper = styled.img`
  max-width: 100%;
  display: block;
  margin: 15px auto;
`;

const ButtonWrapper = styled.div`
  padding-top: 5px;
`;

// ID: 1 = pełna, 2 = połówka, 3 = pusta (uzgadniane z backendem)
const WALL_TYPE_OPTIONS = [
  { id: 1, key: 'roofing_wall_type_full' },
  { id: 2, key: 'roofing_wall_type_half' },
  { id: 3, key: 'roofing_wall_type_none' },
];

const ConstructionExtras = ({
                              collapsed,
                              loaded = false,
                              roofing_available = [],
                              roofing_placement_available = [],
                              roofing_info = null,
                              roofing_selected = null,
                              roofing_placement_selected = null,
                              setSelected,
                              t
                            }) => {
  const dispatch = useDispatch();

  const roofingExtension = useSelector(
    (state) => state.Construction.roofingExtension || {}
  );
  const {
    wall_left_type,
    wall_front_type,
    wall_right_type,
  } = roofingExtension;

  const openModal = () => {
    Modal.info({
      title: roofing_info?.info_title,
      content: (
        <>
          {roofing_info ? (
            <>
              {roofing_info.img_one && <ImgWrapper src={roofing_info.img_one} />}
              {roofing_info.img_two && <ImgWrapper src={roofing_info.img_two} />}
              {roofing_info.info_content}
              {roofing_info.img_three && <ImgWrapper src={roofing_info.img_three} />}
              {roofing_info.img_four && <ImgWrapper src={roofing_info.img_four} />}
            </>
          ) : null}
        </>
      )
    });
  };

  const getWallTypeOptions = () =>
    WALL_TYPE_OPTIONS.map((opt) => ({
      id: opt.id,
      value: t(opt.key) || opt.key,
    }));

  const handleWallLeftTypeChange = (id) => {
    dispatch(setRoofingExtension({ wall_left_type: id }));
  };

  const handleWallFrontTypeChange = (id) => {
    dispatch(setRoofingExtension({ wall_front_type: id }));
  };

  const handleWallRightTypeChange = (id) => {
    dispatch(setRoofingExtension({ wall_right_type: id }));
  };

  // ⬇⬇⬇ NOWE: zmiana placementu – ustawiamy również roofingExtension.placement
  const handlePlacementChange = (id) => {
    setSelected('roofing_placement', id);            // to co było – dla backendu
    dispatch(setRoofingExtension({ placement: id })); // NOWE – dla 3D
  };

  // DEFAULTY typów ścian – jak wcześniej
  useEffect(() => {
    if (!loaded) return;

    // tylko gdy zadaszenie WYBRANE (czyli nie "brak zadaszenia")
    if (roofing_selected === 1 || roofing_selected == null) return;

    // jeśli cokolwiek już ustawione, nie nadpisujemy
    if (wall_left_type != null || wall_front_type != null || wall_right_type != null) {
      return;
    }

    dispatch(setRoofingExtension({
      wall_left_type: 1,   // pełna
      wall_front_type: 3,  // pusta
      wall_right_type: 1,  // pełna
    }));
  }, [
    loaded,
    roofing_selected,
    wall_left_type,
    wall_front_type,
    wall_right_type,
    dispatch,
  ]);

  return (
    <>
      {loaded ? (
        <EditableSection
          collapsed={collapsed}
          title={t('additional_roofing_at_garage')}
          nested
          subsection
          open={false}
        >
          <Spacer>
            {t('please_describe_location_of_canopy')}
          </Spacer>

          {/* SIZE (długość zadaszenia) */}
          <SelectEdit
            selected={roofing_selected}
            loaded={loaded}
            title={roofing_info?.name}
            onChange={(id) => { setSelected('roofing', id); }}
            options={roofing_available.map((el) => ({
              id: el.id,
              value: `${el.attributes.value_cm}cm`
            }))}
            nested
          />

          {/* PLACEMENT */}
          {roofing_selected !== 1 && (
            <SelectEdit
              selected={roofing_placement_selected}
              loaded={loaded}
              title={t('roofing_placement')}
              onChange={handlePlacementChange}
              options={roofing_placement_available.map((el) => ({
                id: el.id,
                value: t(`${el.attributes.value}`),
              }))}
              nested
            />
          )}

          {/* 3 selecty – typy ścian pod zadaszeniem */}
          {roofing_selected !== 1 && (
            <>
              <SelectEdit
                selected={wall_left_type}
                loaded={loaded}
                title={t('roofing_wall_left_type') || 'Typ ściany lewej pod zadaszeniem'}
                onChange={handleWallLeftTypeChange}
                options={getWallTypeOptions()}
                nested
              />
              <SelectEdit
                selected={wall_front_type}
                loaded={loaded}
                title={t('roofing_wall_front_type') || 'Typ ściany frontowej pod zadaszeniem'}
                onChange={handleWallFrontTypeChange}
                options={getWallTypeOptions()}
                nested
              />
              <SelectEdit
                selected={wall_right_type}
                loaded={loaded}
                title={t('roofing_wall_right_type') || 'Typ ściany prawej pod zadaszeniem'}
                onChange={handleWallRightTypeChange}
                options={getWallTypeOptions()}
                nested
              />
            </>
          )}

          {roofing_selected !== 1 && (
            <Info>{t('color_of_additional_canopy')} </Info>
          )}

          <Container>
            <ButtonWrapper>
              <Button
                icon={<InfoCircleOutlined />}
                type="primary"
                block
                onClick={openModal}
              >
                {t('more_information')}
              </Button>
            </ButtonWrapper>
          </Container>
        </EditableSection>
      ) : null}
    </>
  );
};

ConstructionExtras.propTypes = {
  collapsed: PropTypes.bool.isRequired,
  setSelected: PropTypes.func.isRequired,
  loaded: PropTypes.bool,
  roofing_available: PropTypes.arrayOf(availableType),
  roofing_placement_available: PropTypes.arrayOf(availableType),
  roofing_info: infoShape,
  roofing_selected: PropTypes.number,
  roofing_placement_selected: PropTypes.number,
};

const mapDispatchToProps = {
  setSelected: setSelectedParameter,
};

export default connect(selector, mapDispatchToProps)(withTranslation(ConstructionExtras));
